import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/AuthContext";
import { feedbackService } from "@/api/services/feedbackService";
import { toast } from "sonner";
import {
  HelpCircle, MessageSquarePlus, Search, BookOpen,
  Bug, Lightbulb, ThumbsUp, MessageCircle, ChevronRight,
  Mail, FileText, Star,
} from "lucide-react";

// Inline FAQ data for search (mirrors FAQ.jsx categories)
const FAQ_ITEMS = [
  { q: "What is FindGambia?", a: "FindGambia is The Gambia's business discovery platform — find restaurants, hotels, shops, and more.", page: "FAQ" },
  { q: "Do I need an account to browse?", a: "No account is needed to browse. Create a free account to save favourites, write reviews, and make bookings.", page: "FAQ" },
  { q: "Is FindGambia free to use?", a: "Yes! FindGambia is completely free for users.", page: "FAQ" },
  { q: "How do I write a review?", a: "Visit the business page, scroll to the Reviews section, and click 'Write a Review'. You must be logged in.", page: "FAQ" },
  { q: "How do I add my business?", a: "Sign up or log in, then click 'Add Business' in your account menu. Fill in your details and submit for review.", page: "FAQ" },
  { q: "How do I make a booking?", a: "Find the business, click 'Book Now', choose your date/time, and confirm. You will receive a confirmation email.", page: "FAQ" },
  { q: "How do I save a business?", a: "Click the bookmark icon on any business card or listing page. Find saved businesses in your dashboard.", page: "FAQ" },
  { q: "How do I reset my password?", a: "Click 'Sign In' → 'Forgot password?' and enter your email. Check your inbox for a reset link.", page: "FAQ" },
  { q: "How do businesses get verified?", a: "Business owners can request verification from their dashboard. Our team reviews the submission.", page: "FAQ" },
  { q: "How do I cancel a booking?", a: "Go to My Bookings in your dashboard and click Cancel on the relevant booking.", page: "FAQ" },
  { q: "How do I report a problem or inaccurate listing?", a: "Click the flag/report icon on the business page or contact us directly through the Contact page.", page: "Contact" },
  { q: "How do I delete my account?", a: "Go to Profile Settings → Privacy → Request Data Deletion, or contact us at support@findgambia.com.", page: "PrivacyChoices" },
];

const FEEDBACK_TYPES = [
  { id: "BUG_REPORT",       label: "Bug Report",       icon: Bug,              color: "text-red-500 bg-red-50 border-red-200" },
  { id: "FEATURE_REQUEST",  label: "Feature Request",  icon: Lightbulb,        color: "text-amber-500 bg-amber-50 border-amber-200" },
  { id: "SUGGESTION",       label: "Suggestion",       icon: MessageCircle,    color: "text-blue-500 bg-blue-50 border-blue-200" },
  { id: "COMPLIMENT",       label: "Compliment",       icon: ThumbsUp,         color: "text-green-500 bg-green-50 border-green-200" },
  { id: "GENERAL",          label: "General",          icon: MessageSquarePlus,color: "text-muted-foreground bg-muted/50 border-border" },
];

// Controlled panel — the floating trigger lives in ContactSpeedDial, which opens
// this via the `open` / `onOpenChange` props. Renders only the panel, no FAB.
export default function FeedbackHelpWidget({ open = false, onOpenChange = () => {} }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("help"); // "help" | "feedback"
  const [query, setQuery] = useState("");
  const [feedbackType, setFeedbackType] = useState("GENERAL");
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const panelRef = useRef(null);

  // Sync email field when user logs in/out
  useEffect(() => {
    setEmail(user?.email || "");
  }, [user?.email]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      // Ignore clicks on the speed-dial trigger so it can toggle the panel itself.
      if (e.target.closest?.("[data-speed-dial]")) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) onOpenChange(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reset all state when panel closes
  useEffect(() => {
    if (!open) {
      setSubmitted(false);
      setMessage("");
      setRating(0);
      setFeedbackType("GENERAL");
      setQuery("");
      setTab("help");
    }
  }, [open]);

  const filteredFaq = query.trim().length > 1
    ? FAQ_ITEMS.filter((item) =>
        item.q.toLowerCase().includes(query.toLowerCase()) ||
        item.a.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 5)
    : [];

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await feedbackService.submit({
        type: feedbackType,
        message: message.trim(),
        senderEmail: user?.email || email || undefined,
        senderName: user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : undefined,
        rating: rating > 0 ? rating : undefined,
      });
      setSubmitted(true);
      toast.success("Feedback received — thank you!");
    } catch {
      toast.error("Failed to send feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Panel (trigger is provided by ContactSpeedDial) */}
      {open && (
        <div
          ref={panelRef}
          className="fixed right-6 z-40 w-[340px] max-w-[calc(100vw-2rem)] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{ bottom: "5rem" }}
        >
          {/* Header tabs */}
          <div className="flex border-b border-border">
            <button
              onClick={() => setTab("help")}
              className={`flex-1 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                tab === "help"
                  ? "text-primary border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <HelpCircle className="h-4 w-4" /> Help
            </button>
            <button
              onClick={() => setTab("feedback")}
              className={`flex-1 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                tab === "feedback"
                  ? "text-primary border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <MessageSquarePlus className="h-4 w-4" /> Feedback
            </button>
          </div>

          {/* ── HELP TAB ── */}
          {tab === "help" && (
            <div className="flex flex-col max-h-[420px] overflow-y-auto">
              {/* Search */}
              <div className="p-3 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search for help..."
                    className="pl-8 h-9 rounded-lg text-sm"
                    autoFocus
                  />
                </div>
              </div>

              {/* Search results */}
              {filteredFaq.length > 0 && (
                <div className="p-2 border-b border-border">
                  <p className="text-xs text-muted-foreground px-2 pb-1">Results</p>
                  {filteredFaq.map((item, i) => (
                    <button
                      key={i}
                      onClick={() => { navigate(createPageUrl(item.page)); onOpenChange(false); }}
                      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <p className="text-sm font-medium text-foreground leading-snug">{item.q}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.a}</p>
                    </button>
                  ))}
                </div>
              )}

              {query.length > 1 && filteredFaq.length === 0 && (
                <div className="px-4 py-3 text-sm text-muted-foreground border-b border-border">
                  No results for "{query}".{" "}
                  <button onClick={() => setTab("feedback")} className="text-primary hover:underline">
                    Send us a message
                  </button>
                </div>
              )}

              {/* Quick links */}
              <div className="p-2">
                <p className="text-xs text-muted-foreground px-2 py-1.5 font-medium uppercase tracking-wide">Resources</p>
                {[
                  { label: "Frequently Asked Questions", icon: HelpCircle, page: "FAQ" },
                  { label: "User Guide", icon: BookOpen, page: "UserGuide" },
                  { label: "Content Guidelines", icon: FileText, page: "ContentGuidelines" },
                ].map((link) => (
                  <Link
                    key={link.page}
                    to={createPageUrl(link.page)}
                    onClick={() => onOpenChange(false)}
                    className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex items-center gap-2.5">
                      <link.icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-foreground">{link.label}</span>
                    </div>
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
                  </Link>
                ))}
              </div>

              {/* Contact */}
              <div className="p-2 border-t border-border">
                <p className="text-xs text-muted-foreground px-2 py-1.5 font-medium uppercase tracking-wide">Contact Us</p>
                <Link
                  to={createPageUrl("Contact")}
                  onClick={() => onOpenChange(false)}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors group"
                >
                  <div className="flex items-center gap-2.5">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">Send us a message</span>
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
                </Link>
              </div>
            </div>
          )}

          {/* ── FEEDBACK TAB ── */}
          {tab === "feedback" && (
            <div className="flex flex-col max-h-[420px] overflow-y-auto">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-10 px-6 text-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                    <ThumbsUp className="h-6 w-6 text-green-600" />
                  </div>
                  <p className="font-semibold text-foreground">Thank you!</p>
                  <p className="text-sm text-muted-foreground">Your feedback helps us improve FindGambia.</p>
                  <Button size="sm" variant="outline" className="rounded-lg mt-2" onClick={() => { setSubmitted(false); setMessage(""); setRating(0); setFeedbackType("GENERAL"); }}>
                    Send more feedback
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleFeedbackSubmit} className="p-4 space-y-4">
                  {/* Type selector */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">What type of feedback?</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {FEEDBACK_TYPES.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setFeedbackType(t.id)}
                          className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg border text-xs font-medium transition-colors ${
                            feedbackType === t.id ? t.color + " ring-1 ring-inset" : "border-border text-muted-foreground hover:bg-muted/50"
                          }`}
                        >
                          <t.icon className="h-3.5 w-3.5" />
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Star rating — show for COMPLIMENT or GENERAL */}
                  {(feedbackType === "COMPLIMENT" || feedbackType === "GENERAL") && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">Overall rating (optional)</p>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setRating(s === rating ? 0 : s)}
                            className="focus:outline-none"
                          >
                            <Star
                              className={`h-5 w-5 transition-colors ${s <= rating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/40"}`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Message */}
                  <div>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={
                        feedbackType === "BUG_REPORT"
                          ? "Describe what happened and how to reproduce it..."
                          : feedbackType === "FEATURE_REQUEST"
                          ? "What feature would you like to see?"
                          : "Share your thoughts..."
                      }
                      className="min-h-[90px] rounded-xl text-sm resize-none"
                      required
                    />
                  </div>

                  {/* Email — only if not logged in */}
                  {!user && (
                    <div>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Your email (optional)"
                        className="rounded-xl h-9 text-sm"
                      />
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting || !message.trim()}
                    className="w-full rounded-xl"
                    size="sm"
                  >
                    {submitting ? (
                      <span className="h-3.5 w-3.5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      "Send Feedback"
                    )}
                  </Button>
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
